import {
  createBaseThemeOptions,
  palettes,
  createUnifiedTheme,
} from '@backstage/theme';
import { themeColors } from './design-colors';
import {
  commonTypography,
  commonComponents,
  commonSidebarItemRootStyles,
  commonSidebarSubmenuItemStyles,
  commonSidebarLabelStyles,
  commonSidebarLinkResetStyles,
  createTableHeaderOverrides,
} from './common';

export const dtLightTheme = createUnifiedTheme({
  ...createBaseThemeOptions({
    palette: {
      ...palettes.light,
      border: themeColors.lightGrey20k,
      background: {
        default: themeColors.lightGrey,
        paper: themeColors.white,
      },
      text: {
        primary: themeColors.darkGrey,
        secondary: themeColors.lightGrey60k,
      },
      action: {
        active: themeColors.petrol,
        hover: themeColors.petrol10,
        selected: themeColors.petrol20,
        disabled: themeColors.lightGrey40k,
        disabledBackground: themeColors.lightGrey20k,
      },
      textContrast: themeColors.darkGrey,
      textSubtle: themeColors.lightGrey60k,

      // Primary brand colors
      primary: {
        main: themeColors.petrol,
        light: themeColors.petrol60,
        dark: themeColors.petrol20k,
        contrastText: themeColors.white,
      },
      secondary: {
        main: themeColors.petrol40,
        light: themeColors.petrol20,
        dark: themeColors.petrol60,
        contrastText: themeColors.darkGrey,
      },

      // Semantic colors
      error: {
        main: themeColors.error,
        light: '#ffebee',
        dark: '#c62828',
        contrastText: themeColors.white,
      },
      warning: {
        main: themeColors.warning,
        light: '#fff3e0',
        dark: '#f57c00',
        contrastText: themeColors.darkGrey,
      },
      success: {
        main: themeColors.success,
        light: '#e8f5e8',
        dark: '#2e7d32',
        contrastText: themeColors.white,
      },
      info: {
        main: themeColors.info,
        light: '#e3f2fd',
        dark: '#1976d2',
        contrastText: themeColors.white,
      },

      gold: themeColors.yellow,

      navigation: {
        background: themeColors.petrol40k,
        indicator: themeColors.yellow,
        color: themeColors.white,
        selectedColor: themeColors.yellow,
        navItem: {
          hoverBackground: themeColors.petrol20k,
        },
        submenu: {
          background: themeColors.petrol,
        },
      },
      divider: themeColors.lightGrey20k,
    },
  }),
  typography: commonTypography,
  components: {
    ...commonComponents,
    ...createTableHeaderOverrides({
      idle: themeColors.lightGrey60k,
      active: themeColors.darkGrey,
    }),
    BackstageHeader: {
      styleOverrides: {
        header: () => ({
          backgroundColor: themeColors.petrol40k,
          backgroundImage: `linear-gradient(135deg, ${themeColors.petrol40k} 0%, ${themeColors.petrol} 50%, ${themeColors.petrol80} 100%)`,
          minHeight: 112,
          boxShadow: '0 2px 8px rgba(0, 67, 85, 0.15)',
          // Force all links to be white
          '& a': {
            color: `${themeColors.white} !important`,
            '&:hover': {
              color: `${themeColors.yellow} !important`,
            },
          },
          // Also target Material-UI components
          '& .MuiLink-root': {
            color: `${themeColors.white} !important`,
            '&:hover': {
              color: `${themeColors.yellow} !important`,
            },
          },
          '& .MuiTypography-colorInherit': {
            color: `${themeColors.white} !important`,
          },
        }),
        title: {
          color: themeColors.white,
          fontWeight: 700,
        },
      },
    },
    BackstageSidebarSubmenuItem: {
      styleOverrides: {
        item: {
          ...commonSidebarSubmenuItemStyles,
          '&:hover': {
            backgroundColor: themeColors.petrol20k,
          },
        },
        selected: {
          backgroundColor: themeColors.petrol,
          color: themeColors.yellow,
          '&:hover': {
            backgroundColor: themeColors.petrol80,
          },
        },
      },
    },
    BackstageSidebarItem: {
      styleOverrides: {
        root: {
          ...commonSidebarItemRootStyles,
          '&.Mui-selected': {
            backgroundColor: themeColors.petrol20k,
            color: themeColors.yellow,
            fontWeight: 600,
            '&:hover': {
              backgroundColor: themeColors.petrol,
            },
            '& .MuiListItemIcon-root': {
              color: themeColors.yellow,
            },
          },
          '&:hover': {
            backgroundColor: themeColors.petrol20k,
            transform: 'translateX(4px)',
          },
        },
        label: {
          ...commonSidebarLabelStyles,
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover' as const,
      },
      styleOverrides: {
        root: {
          textDecoration: 'none',
          fontWeight: 500,
          transition: 'all 0.2s ease-in-out',
          color: themeColors.petrol,
          '&:hover': {
            color: themeColors.petrol20k,
            textDecoration: 'underline',
          },
          '&:active': {
            color: themeColors.petrol40k,
          },
          ...commonSidebarLinkResetStyles,
          // Override for header context to match lifecycle text
          '.BackstageHeader-header &': {
            color: `${themeColors.white} !important`,
            '&:hover': {
              color: `${themeColors.yellow} !important`,
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          transition: 'all 0.3s ease-in-out',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${themeColors.lightGrey20k}`,
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          ...commonComponents.MuiChip.styleOverrides.root,
          color: themeColors.petrol20k,
          backgroundColor: themeColors.petrol10,
          border: `1px solid ${themeColors.petrol40}`,
          '&:hover': {
            backgroundColor: themeColors.petrol20,
            color: themeColors.petrol40k,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          // Target template cards specifically
          '&.MuiPaper-elevation1': {
            '& .MuiCardHeader-root': {
              backgroundColor: themeColors.petrol40k,
              backgroundImage: `linear-gradient(135deg, ${themeColors.petrol40k} 0%, ${themeColors.petrol} 100%)`,
              color: themeColors.white,
              '& .MuiCardHeader-title, & .MuiTypography-h5': {
                color: themeColors.white,
              },
              '& .MuiCardHeader-subheader, & .MuiTypography-body2': {
                color: themeColors.lightGrey20k,
              },
            },
          },
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        // Target the stable part of the class names
        '[class*="BackstageItemCardHeader-root"]': {
          backgroundColor: `${themeColors.petrol40k} !important`,
          backgroundImage: `linear-gradient(135deg, ${themeColors.petrol40k} 0%, ${themeColors.petrol} 100%) !important`,
          color: `${themeColors.white} !important`,
          '& .MuiTypography-root, & h3, & h4': {
            color: `${themeColors.white} !important`,
          },
        },
        // Also target makeStyles header classes
        '[class*="makeStyles-header"]': {
          backgroundColor: `${themeColors.petrol40k} !important`,
          backgroundImage: `linear-gradient(135deg, ${themeColors.petrol40k} 0%, ${themeColors.petrol} 100%) !important`,
          color: `${themeColors.white} !important`,
          '& .MuiTypography-root, & h3, & h4': {
            color: `${themeColors.white} !important`,
          },
        },
        // Target BackstageHeaderLabel value links with multiple selectors
        '[class*="BackstageHeaderLabel-value"] .MuiLink-root': {
          color: `${themeColors.white} !important`,
          textDecoration: 'none !important',
        },
        '[class*="BackstageHeaderLabel-value"] .MuiLink-root:hover': {
          color: `${themeColors.yellow} !important`,
          textDecoration: 'underline !important',
        },
        '[class*="BackstageHeaderLabel-value"] .MuiLink-underlineHover': {
          color: `${themeColors.white} !important`,
          textDecoration: 'none !important',
        },
        '[class*="BackstageHeaderLabel-value"] .MuiLink-underlineHover:hover': {
          color: `${themeColors.yellow} !important`,
          textDecoration: 'underline !important',
        },
        // Also target the header area more broadly
        '[class*="BackstageHeader-header"] .MuiLink-root, [class*="BackstageHeader-header"] .MuiLink-underlineHover':
          {
            color: `${themeColors.white} !important`,
          },
        '[class*="BackstageHeader-header"] .MuiLink-root:hover, [class*="BackstageHeader-header"] .MuiLink-underlineHover:hover':
          {
            color: `${themeColors.yellow} !important`,
          },
        // Target MuiTypography with colorInherit inside header
        '[class*="BackstageHeader-header"] .MuiTypography-colorInherit': {
          color: `${themeColors.white} !important`,
        },
        // Nuclear option - target the specific link path
        'header[class*="BackstageHeader-header"] span[class*="BackstageHeaderLabel-value"] p a':
          {
            color: `${themeColors.white} !important`,
          },
        'header[class*="BackstageHeader-header"] span[class*="BackstageHeaderLabel-value"] p a:hover':
          {
            color: `${themeColors.yellow} !important`,
          },
        // Also try targeting by specific classes combination
        '.MuiTypography-colorInherit.MuiLink-underlineHover': {
          color: `${themeColors.white} !important`,
        },
        // Global override for all links
        body: {
          '& header[class*="BackstageHeader"] a': {
            color: `${themeColors.white} !important`,
          },
          '& header[class*="BackstageHeader"] a:hover': {
            color: `${themeColors.yellow} !important`,
          },
        },
      },
    },
    // ─── CatalogReactUserListPicker ────────────────────────────────────────
    CatalogReactUserListPicker: {
      styleOverrides: {
        root: {
          backgroundColor: themeColors.white,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${themeColors.lightGrey20k}`,
          borderRadius: 8,
        },
        title: {
          color: themeColors.lightGrey60k,
        },
        listIcon: {
          color: themeColors.petrol,
        },
        menuItem: {
          color: themeColors.darkGrey,
          '&:nth-of-type(odd)': {
            backgroundColor: themeColors.white,
          },
          '&:nth-of-type(even)': {
            backgroundColor: themeColors.lightGrey,
          },
          '&.Mui-selected': {
            backgroundColor: themeColors.petrol20,
            color: themeColors.petrol40k,
            '&:hover': {
              backgroundColor: themeColors.petrol40,
            },
          },
          '&:hover': {
            backgroundColor: themeColors.petrol10,
          },
        },
        groupWrapper: {
          backgroundColor: themeColors.lightGrey,
          boxShadow: 'none',
          border: `1px solid ${themeColors.lightGrey20k}`,
          borderRadius: 6,
        },
      },
    },
  },
});
